import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useUnit } from 'effector-react';

import ObjectsMapView from './ObjectsMapView';
import BuildingList from '../Buildings/BuildingList';
import EmptyState from '../EmptyState';
import { reportsApi } from '../../api/reports';
import { buildFileName, downloadBlobAsFile } from '../../utils/download';
import { showSoftSuccess } from '../../utils/softSuccess';
import { $tenantDataStartDate } from '../../store/auth';

const TABS = [
  { id: 'houses', label: 'Дома', Icon: HomeWorkRoundedIcon },
  { id: 'map', label: 'Карта', Icon: MapRoundedIcon },
  { id: 'readings', label: 'Показания', Icon: TableChartRoundedIcon },
];

const hasCoords = (item) =>
  item?.latitude != null &&
  item?.longitude != null &&
  Number.isFinite(Number(item.latitude)) &&
  Number.isFinite(Number(item.longitude));

function currentPeriodValue() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export default function SettlementComplexView({
  complex,
  buildings = [],
  loading = false,
  canEdit = false,
  onAddHouse,
  onOpenHouse,
  onHouseEdited,
}) {
  const tenantDataStartDate = useUnit($tenantDataStartDate);
  const minMonth = tenantDataStartDate ? String(tenantDataStartDate).slice(0, 7) : undefined;
  const [tab, setTab] = useState('houses');
  const [houseSearch, setHouseSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [period, setPeriod] = useState(currentPeriodValue);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [exporting, setExporting] = useState(false);

  const markers = useMemo(() => {
    return (buildings || [])
      .filter(hasCoords)
      .map((building) => {
        const counts = building.device_counts || {};
        return {
          id: `building:${building.id}`,
          kind: 'building',
          title: building.name || 'Дом',
          subtitle: complex?.name || null,
          address: building.address || null,
          latitude: Number(building.latitude),
          longitude: Number(building.longitude),
          complexId: complex?.id,
          buildingId: building.id,
          deviceCount: Number(building.device_count ?? counts.total ?? 0),
          stats: {
            total: Number(building.device_count ?? counts.total ?? 0),
            hvs: Number(counts.hvs ?? 0),
            gvs: Number(counts.gvs ?? 0),
            heating: Number(counts.heating ?? 0),
            electricity: Number(counts.electricity ?? 0),
            heat_allocators: Number(counts.heat_allocators ?? 0),
          },
        };
      });
  }, [buildings, complex?.id, complex?.name]);

  const withoutCoords = useMemo(
    () => (buildings || []).filter((item) => !hasCoords(item)).length,
    [buildings]
  );

  const houseItems = useMemo(
    () =>
      (buildings || []).map((b) => ({
        id: b.id,
        address: b.address,
        name: b.name,
        counters: {
          cold: b.counters?.cold ?? b.device_counts?.hvs ?? 0,
          hot: b.counters?.hot ?? b.device_counts?.gvs ?? 0,
          heat: b.counters?.heat ?? b.device_counts?.heating ?? 0,
          heatAllocators: b.counters?.heatAllocators ?? b.device_counts?.heat_allocators ?? 0,
          electricity: b.counters?.electricity ?? b.device_counts?.electricity ?? 0,
          all: b.counters?.all ?? b.device_counts?.total ?? b.device_count ?? 0,
        },
        incidents: b.incidents ?? { leak: 0, lowBattery: 0, tamper: 0, noReadings: 0 },
        manualSubmissionEnabled: Boolean(b.manual_submission_enabled),
      })),
    [buildings]
  );
  const visibleHouseItems = useMemo(() => {
    const query = houseSearch.trim().toLocaleLowerCase('ru-RU');
    if (!query) return houseItems;
    return houseItems.filter((item) =>
      `${item.name || ''} ${item.address || ''}`.toLocaleLowerCase('ru-RU').includes(query)
    );
  }, [houseItems, houseSearch]);
  const totalDevices = useMemo(
    () => houseItems.reduce((sum, item) => sum + Number(item.counters?.all || 0), 0),
    [houseItems]
  );
  const housesWithoutDevices = useMemo(
    () => houseItems.filter((item) => Number(item.counters?.all || 0) === 0).length,
    [houseItems]
  );

  const loadReport = useCallback(async () => {
    if (!complex?.id) return;
    setReportLoading(true);
    setReportError('');
    try {
      const data = await reportsApi.getResidentialConsumptionReport({
        residentialComplexId: String(complex.id),
        period: period || undefined,
      });
      setReport(data);
    } catch (err) {
      setReport(null);
      setReportError(err?.response?.data?.detail || err?.message || 'Не удалось загрузить показания');
    } finally {
      setReportLoading(false);
    }
  }, [complex?.id, period]);

  useEffect(() => {
    if (tab === 'readings') {
      loadReport();
    }
  }, [tab, loadReport]);

  const handleExport = useCallback(async () => {
    if (!complex?.id) return;
    setExporting(true);
    try {
      const { data, fileName } = await reportsApi.downloadResidentialConsumptionReport({
        residentialComplexId: String(complex.id),
        period: period || undefined,
      });
      const fallback = buildFileName(
        ['settlement', String(complex.name || complex.id), period || 'period'],
        'settlement-readings'
      );
      downloadBlobAsFile(data, fileName?.trim() || `${fallback}.xlsx`);
      showSoftSuccess('Выгрузка готова');
    } catch (err) {
      setReportError(err?.response?.data?.detail || err?.message || 'Не удалось выгрузить');
    } finally {
      setExporting(false);
    }
  }, [complex?.id, complex?.name, period]);

  const handleOpenMarker = (marker) => {
    if (!marker?.buildingId) return;
    setSelectedId(marker.id);
    onOpenHouse?.(marker.buildingId);
  };

  return (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          borderRadius: 3,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
        }}
      >
        {TABS.map(({ id, label, Icon }) => (
          <Button
            key={id}
            size="small"
            variant={tab === id ? 'contained' : 'text'}
            startIcon={<Icon fontSize="small" />}
            onClick={() => setTab(id)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {label}
          </Button>
        ))}
        <Box sx={{ flex: 1 }} />
        {canEdit && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={onAddHouse}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Добавить дом
          </Button>
        )}
      </Paper>

      {tab === 'map' && (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Домов: ${buildings.length}`} />
            <Chip size="small" label={`На карте: ${markers.length}`} color={markers.length ? 'primary' : 'default'} />
            {withoutCoords > 0 && (
              <Chip size="small" color="warning" label={`Без координат: ${withoutCoords}`} />
            )}
          </Stack>
          {!loading && !markers.length ? (
            <EmptyState
              icon="house"
              title="На карте пока пусто"
              subtitle="Добавьте дом с адресом из подсказки"
              actionLabel={canEdit ? 'Добавить дом' : undefined}
              onAction={canEdit ? onAddHouse : undefined}
            />
          ) : (
            <ObjectsMapView
              markers={markers}
              selectedId={selectedId}
              onSelect={(marker) => setSelectedId(marker?.id || null)}
              onOpen={handleOpenMarker}
              height={520}
            />
          )}
        </Stack>
      )}

      {tab === 'houses' && (
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              value={houseSearch}
              onChange={(event) => setHouseSearch(event.target.value)}
              placeholder="Поиск по адресу или названию"
              sx={{ width: { xs: '100%', sm: 360 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`Домов: ${houseItems.length}`} />
              <Chip size="small" label={`Приборов: ${totalDevices}`} color="primary" variant="outlined" />
              {housesWithoutDevices > 0 ? (
                <Chip size="small" label={`Без приборов: ${housesWithoutDevices}`} color="warning" variant="outlined" />
              ) : null}
            </Stack>
          </Stack>
          <BuildingList
            items={visibleHouseItems}
            loading={loading}
            emptyActionLabel={!houseSearch && canEdit ? 'Добавить дом' : undefined}
            onEmptyAction={!houseSearch && canEdit ? onAddHouse : undefined}
            onItemClick={onOpenHouse}
            onEdited={onHouseEdited}
            settlement
          />
        </Stack>
      )}

      {tab === 'readings' && (
        <Stack spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <TextField
                label="Период"
                type="month"
                inputProps={{ min: minMonth }}
                size="small"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
              <Button variant="outlined" onClick={loadReport} disabled={reportLoading}>
                Обновить
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadRoundedIcon />}
                onClick={handleExport}
                disabled={exporting || !complex?.id}
                sx={{ textTransform: 'none' }}
              >
                Excel
              </Button>
            </Stack>
          </Paper>

          {reportError ? <Alert severity="error">{reportError}</Alert> : null}

          {reportLoading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={28} />
            </Stack>
          ) : !report?.rows?.length ? (
            <EmptyState icon="list" title="Нет показаний" subtitle="За выбранный период данных нет" />
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5, maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Дом</TableCell>
                    <TableCell>Прибор</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell align="right">Текущее</TableCell>
                    <TableCell align="right">Предыдущее</TableCell>
                    <TableCell align="right">Расход</TableCell>
                    <TableCell>Статус</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.map((row) => (
                    <TableRow key={`${row.device_id}-${row.apartment_number || ''}-${row.building_name || ''}`}>
                      <TableCell>{row.building_name || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.device_name || row.device_serial || '—'}
                        </Typography>
                        {row.device_serial ? (
                          <Typography variant="caption" color="text.secondary">
                            {row.device_serial}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{row.system_type || '—'}</TableCell>
                      <TableCell align="right">
                        {row.current_value != null ? Number(row.current_value).toLocaleString('ru-RU') : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {row.previous_value != null ? Number(row.previous_value).toLocaleString('ru-RU') : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {row.consumption != null ? Number(row.consumption).toLocaleString('ru-RU') : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status_label || row.status || '—'}
                          color={row.status === 'ok' ? 'success' : row.status === 'missing' ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      )}
    </Stack>
  );
}
